import './TopUpBalanceSheet.sass';

import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { BottomSheet } from '$uikit/BottomSheet/BottomSheet';
import { AmountInput, Currency } from '$shared/components';
import { CurrencyEnum } from '$store/models';
import { mainActions, mainSelector } from '$store/main';
import {
  Button,
  CurrencyIcon,
  List,
  ListItem,
  Markdown,
  Placeholder,
  SegmentedControl,
} from '$uikit';
import { balanceActions, balanceSelector } from '$store/balance';
import {
  PayCurrency,
  PayMethod,
  PayProvider,
  PaymentProviderConfig,
  TopUpMethod,
} from '$store/pay/interface';
import { _confirm, formatCurrency, isTesterId, statReachGoal } from '$utils';
import {
  IcBankCard28,
  IcChevron16,
  IcMir56,
  IcStar28,
  IcVisa56,
  IcMastercard56,
  IcJcb56,
  IcDiscover56,
} from '$assets';
import { getMethodVisual } from '$core/TopUpBalanceSheet/getMethodVisual';
import { useCurrentUserInfo, useTranslator } from '$hooks';
import { toastActions } from '$store/toast';
import { payActions, paySelector } from '$store/pay';
import { SponsorCongratulations } from '$core/BalancePaySheet/SponsorCongratulations/SponsorCongratulations';
import { useHasSubscription } from '$utils/useHasSubscription';
import { useGetAuthorId } from '$utils/useGetAuthorId';
import { Api } from '$api';
import { CardSelectSheet } from '$core/TopUpBalanceSheet/CardSelectSheet';
import { PayInvoiceSheet } from '$core/TopUpBalanceSheet/PayInvoiceSheet';
import { TesterPaymentMethods } from '$core/TopUpBalanceSheet/TesterPaymentMethods';
import { OnlypaySheet } from '$core/OnlypaySheet/OnlypaySheet';
import { paymentMethodsSelector } from '$store/paymentMethods';
import WebApp from '@twa-dev/sdk';
import { balancePaySelector } from '$store/balancePay';
import { actionToTarget, actionToTriggerType } from '$store/balance/sagas';
import { TELEGRAM_BOT_USERNAME } from '$shared/constants';

const TopUpBalanceSheetV2Inner: FC = () => {
  const dispatch = useDispatch();
  const { balance, countryCode, isCryptoEnabled, eurBalance } = useSelector(mainSelector);
  const memoEurBalance = useMemo(() => eurBalance, []);
  const { cards } = useSelector(paymentMethodsSelector);

  const t = useTranslator();
  const { requireTopUpAmount, cryptoPayUrl } = useSelector(balanceSelector);
  const { rates, topUpMethods } = useSelector(paySelector);
  const [method, setMethod] = useState(PayMethod.BALANCE);
  const [popupId, setPopupId] = useState(-1);
  const [isCustomAmountMode, setCustomAmountMode] = useState(false);
  const currentUser = useCurrentUserInfo();
  const isTester = isTesterId(currentUser.id || 0);
  const authorId = useGetAuthorId();
  const hasSubscription = useHasSubscription(authorId);

  const needAmount = requireTopUpAmount
    ? Math.max(
        Math.ceil(
          requireTopUpAmount > memoEurBalance
            ? requireTopUpAmount - memoEurBalance
            : requireTopUpAmount,
        ),
        25,
      )
    : 0;
  const { action, objectId, extraId } = useSelector(balancePaySelector);

  useEffect(() => {
    dispatch(balanceActions.loadTonRate());
    dispatch(payActions.loadTopUpMethods());
    statReachGoal('top_up_balance_opened', { flow: 'payhub' });

    return () => {
      window.isWaitingForPay = false;
    };
  }, []);

  const amounts = [10, 25, 50, 100];
  const maxAmount = amounts[amounts.length - 1];
  const amountList = useMemo(() => {
    if (!needAmount || needAmount >= amounts[maxAmount]) {
      return amounts;
    }
    const nearestValue =
      amounts.reduce(
        (prev, curr) =>
          Math.abs(curr - needAmount) < Math.abs(prev - needAmount) ? curr : prev,
        0,
      ) || amounts[0];
    return amounts.map((value) => (value === nearestValue ? needAmount : value));
  }, [needAmount]);

  const [amount, setAmount] = useState(
    needAmount && needAmount < maxAmount ? needAmount.toString() : amounts[1].toString(),
  );

  const handleClose = useCallback(() => {
    dispatch(balanceActions.setTopUpActive({ topUpActive: false }));
  }, [dispatch]);

  const checkAmount = useCallback(() => {
    if (+amount < 10 && !isTester) {
      dispatch(toastActions.setToastFail(t('top_up_balance_custom_amount_small_err')));
      return true;
    } else if (+amount > 500) {
      dispatch(toastActions.setToastFail(t('top_up_balance_custom_amount_large_err')));
      return true;
    }
    return false;
  }, [isTester, amount]);

  const handleCard = useCallback(
    (card) => {
      if (checkAmount()) return;

      statReachGoal('pay_sheet_card_selected', { amount, flow: 'payhub' });
      dispatch(
        balanceActions.topUp({
          amount: +amount,
          skipAutoPay: false,
          cardId: card?.id,
          provider: PayProvider.tribute,
          onUrl: (url) => {
            handleClose();
            dispatch(mainActions.setPopup(<OnlypaySheet url={url} />));
          },
        }),
      );
    },
    [amount, isTester],
  );

  const handleCustomAmount = () => {
    setCustomAmountMode(true);
  };

  const handlePaymentProvider = (
    config: Omit<PaymentProviderConfig, 'provider'> & { provider?: PayProvider },
  ) => {
    statReachGoal('top_up_payment_initiated', {
      provider: config.provider,
      amount,
      countryCode: config.countryCode,
      paymentMethod: config.paymentMethod,
      flow: 'payhub',
    });

    const request = Api.post<any, any>('/pay/top_up', {
      amount: +amount,
      countryCode: config.countryCode,
      paymentMethod: config.paymentMethod,
      provider: config.provider,
      target: actionToTarget(action!),
      targetObject: +objectId!,
      extraId: +extraId,
      triggerPay: actionToTriggerType(action!),
      triggerPayId: +objectId!,
      isMiniApp: window.isWebApp ?? false,
    });

    dispatch(
      mainActions.setPopup(<PayInvoiceSheet request={request} onSuccess={handleClose} />),
    );
  };

  const handleDynamicMethod = (m: TopUpMethod) => {
    if (checkAmount()) return;
    handlePaymentProvider({
      countryCode,
      paymentMethod: m.method,
    } as Omit<PaymentProviderConfig, 'provider'>);
  };

  const groupedMethods = useMemo(() => {
    const groups: Record<string, TopUpMethod[]> = {};
    for (const m of topUpMethods) {
      if (!groups[m.region]) groups[m.region] = [];
      groups[m.region].push(m);
    }
    for (const region of Object.keys(groups)) {
      groups[region].sort((a, b) =>
        a.recommended === b.recommended ? 0 : a.recommended ? -1 : 1,
      );
    }
    return Object.keys(groups)
      .sort((a, b) => {
        const aHasRec = groups[a].some((m) => m.recommended);
        const bHasRec = groups[b].some((m) => m.recommended);
        if (aHasRec !== bHasRec) return aHasRec ? -1 : 1;
        if (a === 'ALL' && b !== 'ALL') return 1;
        if (b === 'ALL' && a !== 'ALL') return -1;
        return 0;
      })
      .map((region) => ({ region, methods: groups[region] }));
  }, [topUpMethods]);

  const getRegionTitle = (region: string): string => {
    switch (region) {
      case 'ALL':
        return t('balance_for_world_title');
      case 'RU':
        return t('balance_for_russia_title');
      case 'BR':
        return t('balance_for_brazil_title');
      case 'MX':
        return t('balance_for_mexico_title');
      default:
        return region;
    }
  };

  const amountTip = useMemo(() => {
    if (+amount < 10) {
      return t('top_up_balance_custom_amount_small_err');
    } else if (+amount > 500) {
      return t('top_up_balance_custom_amount_large_err');
    }
    return t('top_up_balance_custom_amount_tip');
  }, [amount]);

  const cryptoPayCurrencies = [PayCurrency.TON, PayCurrency.BTC, PayCurrency.USDT];

  useEffect(() => {
    if (hasSubscription) {
      setPopupId(0);
    }
  }, [hasSubscription]);

  const handleTopUpFromTonAmount = () => {
    const requireTopUpTonAmount = rates['eur']['ton'] * +amount;
    if (balance >= requireTopUpTonAmount) {
      _confirm(t('top_up_balance_from_ton_confirm', { amount: amount }), () => {
        dispatch(balanceActions.topUpFromTonBalance(+amount));
      });
    } else {
      dispatch(
        balanceActions.setTopUpTonActive({
          topUpActive: true,
          requireTopUpAmount: requireTopUpTonAmount - balance,
        }),
      );
    }
  };

  function convertLink(url) {
    const pattern = /https:\/\/t\.me\/\$(.+)/;
    const match = url.match(pattern);
    if (match && match[1]) {
      return `tg://invoice?slug=${match[1]}`;
    } else {
      return null;
    }
  }

  const handleTelegram = () => {
    dispatch(toastActions.setToastLoading());
    Api.post<any, any>('/pay/stars/top_up', {
      amount: +amount,
      target: actionToTarget(action!),
      targetObject: +objectId!,
      extraId: +extraId,
      triggerPay: actionToTriggerType(action!),
      triggerPayId: +objectId!,
    })
      .then((resp) => {
        if (window.isWebApp) {
          setTimeout(() => {
            WebApp.openInvoice(JSON.parse(resp.link));
            handleClose();
            dispatch(toastActions.hideToast());
          }, 1000);
        } else {
          handleClose();
          dispatch(toastActions.hideToast());
          // @ts-ignore
          window.location = convertLink(JSON.parse(resp.link));
        }
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
      });
  };

  const activeCard = useMemo(
    () => cards.find((c) => c.active) || cards[0] || null,
    [cards],
  );

  const handleOpenCardSelect = useCallback(() => {
    if (!activeCard) return;
    dispatch(
      mainActions.setPopup(
        <CardSelectSheet
          cards={cards}
          initialCardId={activeCard.id}
          onConfirm={(cardId) => {
            const card = cards.find((c) => c.id === cardId);
            handleCard(card);
          }}
        />,
      ),
    );
  }, [cards, activeCard, amount]);

  const renderCryptoPayPlaceholder = () => (
    <Placeholder
      actions={
        <div className="TopUpBalanceSheet__cryptoPay__tgActions">
          <a target="_blank" rel="noreferrer" href={cryptoPayUrl!}>
            <Button>{t('payment_cryptopay_pay_button')}</Button>
          </a>
          <Button
            mode="inline"
            onClick={() => dispatch(balanceActions.setCryptoPayUrl(null))}
          >
            {t('back')}
          </Button>
        </div>
      }
    >
      {t('payment_cryptopay_invoice_caption')}
    </Placeholder>
  );

  const renderBalanceHeader = () => (
    <div className="TopUpBalanceSheet__balance">
      {!requireTopUpAmount && (
        <>
          <div className="TopUpBalanceSheet__balance__title">
            {t('balance_available')}
          </div>
          <div className="TopUpBalanceSheet__balance__amount">
            <Currency amount={memoEurBalance} currency={CurrencyEnum.EUR} />
          </div>
        </>
      )}
      {requireTopUpAmount > 0 && requireTopUpAmount > memoEurBalance && (
        <div className="TopUpBalanceSheet__balance__amount_tip">
          {t('top_up_balance_need_amount', {
            amount: formatCurrency(requireTopUpAmount, CurrencyEnum.EUR),
          })}
        </div>
      )}
    </div>
  );

  const renderSegmentedControl = () =>
    isCryptoEnabled && (
      <div style={{ marginTop: 16 }}>
        <SegmentedControl
          options={[
            { label: t('top_up_balance_pay_via_card'), value: PayMethod.BALANCE },
            { label: t('top_up_balance_pay_via_crypto'), value: PayMethod.CRYPTOPAY },
          ]}
          defaultValue="card"
          onChange={(newVal) => setMethod(newVal)}
        />
      </div>
    );

  const renderSavedCard = () => {
    if (!activeCard) return null;
    const icon =
      activeCard.brand === 'mir' ? (
        <IcMir56 />
      ) : activeCard.brand === 'visa' ? (
        <IcVisa56 />
      ) : activeCard.brand === 'mastercard' ? (
        <IcMastercard56 />
      ) : (
        <IcBankCard28 />
      );

    return (
      <List title={t('top_up_balance_saved_cards_title')}>
        <ListItem chevron onClick={handleOpenCardSelect} before={icon}>
          {`•••• ${activeCard.last4 || '****'}`}
        </ListItem>
      </List>
    );
  };

  const renderSmallMethodButton = (m: TopUpMethod) => {
    const visual = getMethodVisual(m.method, t);
    return (
      <div
        key={m.method}
        className={`TopUpBalanceSheet__button${visual.large ? ' mir' : ''}`}
        onClick={() => handleDynamicMethod(m)}
        data-payment-method={m.method}
        data-recommended={m.recommended || undefined}
      >
        <div className="TopUpBalanceSheet__button_icon">{visual.icon}</div>
        <div className="TopUpBalanceSheet__button_label">{visual.label}</div>
      </div>
    );
  };

  const renderWorldGroup = (methods: TopUpMethod[]) => {
    const card = methods.find((m) => m.method === 'card');
    const smalls = methods.filter((m) => m.method !== 'card');

    return (
      <List key="ALL" title={t('balance_for_world_title')}>
        <div className="TopUpBalanceSheet__methods">
          {card && (
            <Button
              onClick={() => handleDynamicMethod(card)}
              className="TopUpBalanceSheet__cardBtn"
              iconRight={
                <>
                  <IcVisa56 />
                  <IcMastercard56 />
                  <IcJcb56 />
                  <IcDiscover56 />
                </>
              }
              mode="pay"
              data-payment-method={card.method}
              data-recommended={card.recommended || undefined}
            >
              <IcBankCard28 />
              {t('top_up_method_singularity')}
            </Button>
          )}
          <Button onClick={handleTelegram} icon={<IcStar28 />}>
            Telegram Stars
          </Button>
          {smalls.length > 0 && (
            <div className="TopUpBalanceSheet_group">
              {smalls.map(renderSmallMethodButton)}
            </div>
          )}
        </div>
      </List>
    );
  };

  const renderRegionGroup = (region: string, methods: TopUpMethod[]) => (
    <List key={region} title={getRegionTitle(region)}>
      <div className="TopUpBalanceSheet__methods">
        <div className="TopUpBalanceSheet_group">
          {methods.map(renderSmallMethodButton)}
        </div>
      </div>
    </List>
  );

  const renderDynamicMethods = () => {
    const hasWorldGroup = groupedMethods.some((g) => g.region === 'ALL');
    return (
      <>
        {groupedMethods.map(({ region, methods }) =>
          region === 'ALL'
            ? renderWorldGroup(methods)
            : renderRegionGroup(region, methods),
        )}
        {!hasWorldGroup && renderWorldGroup([])}
      </>
    );
  };

  const renderTesterSection = () => {
    if (!isTester) return null;
    return (
      <TesterPaymentMethods countryCode={countryCode} onSelect={handlePaymentProvider} />
    );
  };

  const renderBalanceTab = () => (
    <>
      <div className="PaySheet__button_wrap">
        {renderSavedCard()}
        {renderDynamicMethods()}
        {renderTesterSection()}
      </div>
      <div className="TopUpBalanceSheet__fee">
        <Markdown>{t('top_up_balance_fee_tip')}</Markdown>
      </div>
    </>
  );

  const renderCryptoPayTab = () => (
    <div className="TopUpBalanceSheet__selectCurrency">
      <List title={t('top_up_balance_from_ton_title')}>
        <ListItem
          icon={<IcChevron16 />}
          iconSize="small"
          onClick={handleTopUpFromTonAmount}
          before={<CurrencyIcon currency="ton" />}
          description={
            <span className="TopUpBalanceSheet__balanceAmount">
              {t('top_up_balance_from_ton_balance')}:{' '}
              <Currency amount={balance} currency="ton" />
            </span>
          }
        >
          {`${formatCurrency(amount, CurrencyEnum.EUR)} `}
          <Currency nearly amount={rates['eur']['ton'] * +amount} currency="ton" />
        </ListItem>
      </List>
      <List>
        <ListItem
          href={`https://t.me/${TELEGRAM_BOT_USERNAME}?start=pay-${+amount * 100}-${
            currentUser.id
          }-1`}
          target="_blank"
          description={t('top_up_method_wallet_pay_caption')}
          chevron
          color="green"
        >
          Wallet Pay
        </ListItem>
      </List>
      <List title={balance ? t('top_up_balance_cryptobot_title') : undefined}>
        {cryptoPayCurrencies.map((c) => (
          <ListItem
            key={c}
            icon={<IcChevron16 />}
            iconSize="small"
            onClick={() =>
              dispatch(balanceActions.cryptoBayTopUp({ currency: c, amount: +amount }))
            }
            before={<CurrencyIcon currency={c} />}
          >
            <Currency
              nearly
              amount={rates['eur'][c.toLowerCase()] * +amount}
              currency={c}
            />
          </ListItem>
        ))}
      </List>
    </div>
  );

  const renderContent = () => {
    if (cryptoPayUrl) {
      return renderCryptoPayPlaceholder();
    }

    return (
      <div className="TopUpBalanceSheet">
        {renderBalanceHeader()}
        <AmountInput
          amount={amount}
          onInputChange={(value) => setAmount(value)}
          amountList={amountList}
          currency={CurrencyEnum.EUR}
          title={t('top_up_balance_sub_title')}
          inputTip={amountTip}
          placeholder={t('top_up_balance_custom_amount_placeholder')}
          isCustomAmountMode={isCustomAmountMode}
          onInlineButtonClick={handleCustomAmount}
        />
        {renderSegmentedControl()}
        {method === PayMethod.BALANCE ? renderBalanceTab() : renderCryptoPayTab()}
        <div className="TopUpBalanceSheet__warning">{t('top_up_balance_warning')}</div>
      </div>
    );
  };

  return (
    <BottomSheet
      popupId={popupId}
      setPopupId={setPopupId}
      chain={[<SponsorCongratulations key={'sponsor'} />]}
      title={t('top_up_balance_title')}
      onClose={handleClose}
    >
      {renderContent()}
    </BottomSheet>
  );
};

export const TopUpBalanceSheetV2: FC = () => {
  return <TopUpBalanceSheetV2Inner />;
};
